import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState } from 'react';

import { BadRequestError } from '_lib/api/apiErrors';
import { ContactDTO, ST, ContactDtoSchema } from '_lib/api/apiTypes.shim';

import { CircularProgress } from '_lib/components/CircularProgress/CircularProgress';
import { Grid } from '_lib/components/Grid/Grid';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { PhoneNumberTextField } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { TextField, TextFieldProps } from '_lib/components/TextField/TextField';
import { reportException } from '_lib/utils/sentryService';

type APISave<T> = (data: T, setAPIErrors: (err: BadRequestError) => void) => void;

export interface EditableContactProps<T extends ContactDTO> {
  contact: T;
  isDisabled: boolean;
  onSaveContact: APISave<T>;
  onCancelEditContact: () => void;
  onContactUpdate?: (contact: T) => void;
}

const typeNameComponent = (contactTypeName: ST.ContactTypeEnum, props: TextFieldProps) => {
  if (contactTypeName === ST.ContactTypeEnum.Email) return <TextField {...props} />;
  return <PhoneNumberTextField {...props} />;
};

export const EditableContact = <T extends ContactDTO>(props: EditableContactProps<T>): React.ReactElement => {
  const { contact, onContactUpdate, onSaveContact, onCancelEditContact, isDisabled } = props;

  const [contactCopy, setContactCopy] = useState<T>({ ...contact });
  const [contactError, setContactError] = useState('');
  const [showError, setShowError] = useState(false);

  const handleFieldChange = (changes: Partial<ContactDTO>) => {
    const contactCopyUpdated = { ...contactCopy, ...changes };
    setContactCopy(contactCopyUpdated);
    if (onContactUpdate) onContactUpdate(contactCopyUpdated);

    setContactError('');
    const parseResult = ContactDtoSchema.safeParse(contactCopyUpdated);
    if (!parseResult.success) {
      const allSchemaErrors = parseResult.error.issues.map(({ message }) => message).join(', ') || 'Unexpected Error';
      setContactError(allSchemaErrors);
    }
  };

  /**
   * Also set contact error if we get a bad request with a field path 'value'
   * @param err
   */
  const setAPIErrors = (err: BadRequestError) => {
    const fieldIssues = err.responseData?.fieldIssues || [];
    const nonFieldErrors = err.responseData?.nonFieldErrors || [];
    if (fieldIssues.length || nonFieldErrors.length) {
      const issueErrors: string[] = fieldIssues.map(({ message }) => message);
      const allErrors = issueErrors.concat(nonFieldErrors).join(', ') || 'Unexpected Error';
      setContactError(allErrors);
      setShowError(true); // Always show Errors if we have an API error
    } else {
      reportException(new Error('Unexpected BadRequestError - EditableContact'), {
        extra: {
          responseData: err.responseData,
          axiosErrorCode: err.axiosError.code,
          axiosErrorStatus: err.axiosError.response?.status
        }
      });
    }
  };

  const _submit = () => {
    if (contactError || !contactCopy.value) {
      setShowError(true); // Only show validation errors after first save attempt
      return;
    }
    onSaveContact(contactCopy, setAPIErrors);
  };

  return (
    <Grid container spacing={1}>
      <Grid item sm={'auto'} xs={'auto'}>
        <TextField
          fullWidth
          disabled={isDisabled}
          value={contactCopy.type}
          select
          onChange={(event) => handleFieldChange({ type: event.target.value as ST.ContactTypeEnum })}>
          {Object.values(ST.ContactTypeEnum).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item sm={true} xs={true}>
        {typeNameComponent(contactCopy.type, {
          fullWidth: true,
          disabled: isDisabled,
          value: contactCopy.value,
          error: contactError && showError ? true : false,
          helperText: contactError && showError ? contactError : '',
          onChange: (event) => handleFieldChange({ value: event.target.value }),
          onEnter: _submit
        })}
      </Grid>
      <Grid item sm={'auto'} xs={'auto'} sx={{ alignSelf: 'center' }}>
        <IconButton onClick={_submit}>
          <SaveIcon />
          {isDisabled && <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} />}
        </IconButton>
        <IconButton onClick={onCancelEditContact} disabled={isDisabled}>
          <CloseIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};
